import api from './api';
import router from '../../router';
import { UnauthorizedError } from '../../common/error/errors.js';

//Path Regex to check if socket listener should be used
const pathRegex = /^\/solution/;

const state = {
	error: null,
	isLoading: false,
	vacationBalance: null,
	freeUsers: {},
};

const getters = {};

const mutations = {
	setError(state, { error }) {
		if (error.response?.data?.message) {
			state.error = error.response.data.message;
		} else {
			state.error = error.message;
		}
	},

	clearError(state) {
		state.error = null;
	},

	setLoading(state, { loading }) {
		state.isLoading = loading;
	},

	setAvailableVacation(state, { vacationBalance }) {
		state.vacationBalance = vacationBalance;
	},

	setFreeUsers(state, freeUsers) {
		state.freeUsers = freeUsers;
	},
};

const actions = {
	setLoading({ commit }, loading) {
		commit('setLoading', { loading });
	},
	async getAbsence({ state, commit, rootGetters }, absenceId) {
		commit('clearError');
		try {
			const absence = await api.loadAbsence(absenceId);
			return absence.data;
		} catch (error) {
			commit('setError', { error });
			return {};
		}
	},
	async getReport({ state, commit, rootGetters }, reportId) {
		try {
			const report = await api.loadReport(reportId);
			return report.data;
		} catch (error) {
			commit('setError', { error });
			return {};
		}
	},
	async getFreeUsers({ commit }, absenceId) {
		try {
			const freeUsers = await api.loadFreeUsers(absenceId);
			commit('setFreeUsers', freeUsers.data);
		} catch (error) {
			commit('setError', { error });
		}
	},
	async updateAbsence({ dispatch, commit }, absence) {
		try {
			let result = await dispatch('userActions/updateAbsence', absence, { root: true });
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},
	async setNotificationToIgnore({ commit }, { notification }) {
		try {
			let updatedNotification = {
				_id: notification._id,
				ignored: true,
			};
			const result = await api.updateNotification(updatedNotification);
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async updateReport({ commit }, report) {
		try {
			const result = await api.updateReport(report);
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async getAlternativeSolutionDriver({ state, commit, rootGetters }, { dutyId }) {
		commit('clearError');
		try {
			commit('setLoading', { loading: true });
			let result = await api.loadAlternativeSolutionFindDriver(dutyId);
			commit('setLoading', { loading: false });
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async getAlternativeSolutionOfDriver({ state, commit, rootGetters }, { dutyId, driverId }) {
		try {
			commit('setLoading', { loading: true });
			let result = await api.loadAlternativeSolutionOfDriver(dutyId, driverId);
			commit('setLoading', { loading: false });
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async setDutyForDriver({ state, commit, rootGetters }, { dutyId, driverId, type, day }) {
		commit('clearError');
		commit('setLoading', { loading: true });
		try {
			let result = await api.assignDuty(dutyId, driverId, type, day);
			return result;
		} catch (error) {
			commit('setError', { error });
			return null;
		} finally {
			commit('setLoading', { loading: false });
		}
	},

	async getAlternativeSolutionDuty({ state, commit, rootGetters }, { driverId, dayId }) {
		commit('clearError');
		try {
			commit('setLoading', { loading: true });
			let result = await api.loadAlternativeSolutionFindDuty(driverId, dayId);
			commit('setLoading', { loading: false });
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async getDutiesOfDay({ state, commit, rootGetters }, { driverId, dayId }) {
		commit('clearError');
		try {
			let result = await api.loadDutiesFromDay(dayId);
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async getAlternativeSolutionOfDuty({ state, commit, rootGetters }, { dutyId, driverId, dayId }) {
		try {
			commit('setLoading', { loading: true });
			let result = await api.loadAlternativeSolutionOfDuty(dutyId, driverId, dayId);
			commit('setLoading', { loading: false });
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async getLastStatisticsOfYear({ state, commit }, { user, year }) {
		try {
			const result = await api.loadLastStatisticsOfYear(user, year);
			if (result) {
				commit('setAvailableVacation', { vacationBalance: result.data ? result.data.vacationBalance : 0 });
			}
		} catch (error) {
			commit('setError', { error });
		}
	},

	async setNotificationToDone({ commit }, { notification, reason }) {
		try {
			let updatedNotification = {
				_id: notification._id,
				done: true,
			};

			if (reason) {
				const { params = {} } = notification;

				params.reason = reason;
				updatedNotification.params = params;
			}

			let result = await api.updateNotification(updatedNotification);
			return result;
		} catch (error) {
			commit('setError', { error });
		}
	},

	async setAllNotificationsToDone({ commit }, { type, userId, reason }) {
		try {
			const result = await api.setAllNotificationsToDone(type, userId, reason);
			return result;
		} catch (error) {
			commit('setError', { error });
			return {};
		}
	},
	async getDocumentByID({ state, commit, rootGetters }, id) {
		try {
			if (typeof id !== 'string') {
				const message = `The type of the document-id is '${typeof id}', but should be 'string' (getDocumentByID).`;
				console.error(message);
				throw new Error(message);
			}
			const response = await api.getDocumentByID(id);
			return response.data;
		} catch (err) {
			if (err.response?.request?.status !== 401) {
				const message = err.response?.data?.error?.message || 'An unexpected error occurred.';
				commit('setError', { message });
			}
		}
	},
	async getExchangeData({ commit }, { users, exchange }) {
		commit('setLoading', { loading: true });

		const fullExchange = await api.loadExchange(exchange);
		if (fullExchange?.data) {
			try {
				const response = await api.evaluateExchange(fullExchange.data.duty, fullExchange.data.receiver);
				if (response.data) {
					const exchangeData = { users: [fullExchange.data.receiver] };
					if (fullExchange.data.sender) {
						exchangeData.users.unshift(fullExchange.data.sender);
					}
					exchangeData.violations = response.data.violations;
					exchangeData.duties = response.data.duties;
					commit('setLoading', { loading: false });
					return exchangeData;
				}
			} catch (error) {
				commit('setError', { error });
			}
		}

		commit('setLoading', { loading: false });
	},

	async setExchangeStatus({ commit }, { id, status, approvalComment }) {
		try {
			const result = await api.setExchangeStatus(id, status, approvalComment);
			return result;
		} catch (error) {
			commit('setError', { error });
			return {};
		}
	},

	async loadDay({ commit }, dayId) {
		commit('clearError');
		try {
			const result = await api.loadDay(dayId);
			return result.data;
		} catch (error) {
			commit('setError', { error });
			return {};
		}
	},

	async loadDaysByIds({ commit }, { ids, fields }) {
		commit('clearError');
		try {
			const result = await api.loadDaysByIds(ids, fields);
			return result.data;
		} catch (error) {
			commit('setError', { error });
			return [];
		}
	},

	async downloadDocumentById({ commit }, id) {
		try {
			if (typeof id !== 'string') {
				const message = `The type of the document-id is '${typeof id}', but should be 'string' (downloadDocumentById).`;
				console.error(message);
				throw new Error(message);
			}
			const response = await api.downloadDocumentById(id);
			return response.data;
		} catch (error) {
			if (error.response?.request?.status !== 401) {
				commit('setError', { error });
			} else {
				const message = error.message;
				throw new UnauthorizedError(message, error);
			}
		}
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
